import React, { useState, useEffect, useContext } from 'react'
import * as Icons from '../Icons'
import { FeedItem, TeamFrontData } from '../../Api'
import { useAuthContext } from '../../Context'
import { LeagueContext } from '../../Context'
import { teamsDataArray } from '../../Constant/team'
import {
  getAdditionalTeamData,
  getTeamColor,
  getTeamLocation,
  hasWeekendGamesStarted,
  logEvent,
} from '../../Utils'
import { SectionHeading } from '../Headers'
import { Box } from '../Layout'
import { ActionItem, ChangeBtn, HeadLink, SocHeadLink, TmHeader } from './styles'
import { PLL_LINKS } from '../../Config/config'
import { createShopUTM } from '../../Routing'
import { FeedCardList } from '../Feed'
import { colors } from '../consts'

export const FavoriteTeamSection: React.FC<React.PropsWithChildren<unknown>> = () => {
  const { favTeam, clearTeamFavorite } = useAuthContext()
  const { isFetchingTeamFeed, teamFeed, fetchTeamFeed, events } =
    useContext(LeagueContext)!
  const [teamId, setTeamId] = useState<string>('')
  const [favorite, setFavorite] = useState<string | null | undefined>(favTeam)
  const [teamMedia, setTeamMedia] = useState<FeedItem[]>([])
  const [teamData, setTeamData] = useState<TeamFrontData>()

  useEffect(() => {
    if (favTeam && !isFetchingTeamFeed) {
      if (teamFeed.length > 0) {
        setTeamMedia(teamFeed)
      }
    }
  }, [teamFeed])

  useEffect(() => {
    if (favTeam) {
      const teamObj = teamsDataArray.find((tm) => tm.fullName.toLowerCase() === favTeam)
      if (teamObj) {
        const td = getAdditionalTeamData(teamObj.officialId)
        if (!td) return
        setTeamData(td)
        setTeamId(teamObj.officialId)

        if (favTeam !== favorite) {
          // New favorite Team, fetch articles
          fetchMedia(teamObj.officialId)
          setFavorite(favTeam)
        } else {
          if (teamMedia.length === 0) {
            if (teamFeed.length > 0) {
              setTeamMedia(teamFeed)
              return
            }
            fetchMedia(teamObj.officialId)
          }
        }
      }
    }
  }, [favTeam])

  const teamBackground = (
    getTeamColor(teamId) ? `${getTeamColor(teamId)}50` : colors.white
  ) as string

  const fetchMedia = async (team: string) => {
    const feed = await fetchTeamFeed(team)
    if (!feed || feed.length < 1) return
    setTeamMedia(feed)
  }

  const clearFavTeam = () => {
    let weekendStarted = hasWeekendGamesStarted(events)
    if (weekendStarted) {
      alert('Cannot change team while weekend is live. Come back after games end.')
      return
    }
    setFavorite(null)
    clearTeamFavorite()
  }

  const log = (event: string, headerItem: string, link: string) => {
    logEvent(event, {
      section: `${favorite}_Home_section`,
      headerItem,
      link,
    })
  }

  return (
    <>
      {favorite && (
        <Box vertMargins={2} boxColor={teamBackground}>
          <TmHeader>
            <SectionHeading teamColor={getTeamColor(teamId)} lowMargin>
              <strong>
                {`${getTeamLocation(teamId)} `}
                {`${teamData?.fullName}`} Updates
              </strong>
            </SectionHeading>
            {teamData && (
              <div className="actions">
                <HeadLink
                  href={`/schedule`}
                  onClick={() => log('fav_team_section_click', 'schedule', '/schedule')}
                  aria-label={`${favorite} schedule`}
                >
                  <ActionItem>
                    <Icons.Tickets />
                    <p>Schedule</p>
                  </ActionItem>
                </HeadLink>
                <HeadLink
                  href={`/teams/${favorite}/roster`}
                  aria-label={`${favorite} roster`}
                  onClick={() =>
                    log('fav_team_section_click', 'stats', `/teams/${favorite}/roster`)
                  }
                >
                  <ActionItem>
                    <Icons.Clipboard />
                    <p>Roster</p>
                  </ActionItem>
                </HeadLink>
                <HeadLink
                  href={`${PLL_LINKS.stats}/teams/${teamId}`}
                  aria-label={`${favorite} stats`}
                  onClick={() =>
                    log(
                      'fav_team_section_click',
                      'stats',
                      `${PLL_LINKS.stats}/teams/${teamId}`
                    )
                  }
                >
                  <ActionItem>
                    <Icons.BarChart />
                    <p>Stats</p>
                  </ActionItem>
                </HeadLink>
                <HeadLink
                  href={createShopUTM({
                    medium: 'favorite_team_section',
                    campaign: `${favorite}_favorite`,
                    slug: `collections/${favorite}`,
                  })}
                  aria-label={`${favorite} shop`}
                  onClick={() =>
                    log(
                      'fav_team_section_click',
                      'shop',
                      createShopUTM({
                        medium: 'favorite_team_section',
                        campaign: `${favorite}_favorite`,
                        slug: `collections/${favorite}`,
                      })
                    )
                  }
                >
                  <ActionItem>
                    <Icons.ShoppingCart />
                    <p>Shop</p>
                  </ActionItem>
                </HeadLink>
                <SocHeadLink
                  href={teamData?.instagram}
                  aria-label={`${favorite} instagram`}
                  onClick={() =>
                    log('fav_team_section_click', 'instagram', teamData?.instagram || '')
                  }
                >
                  <Icons.Instagram />
                </SocHeadLink>
                <SocHeadLink
                  href={teamData.twitter}
                  aria-label={`${favorite} twitter`}
                  onClick={() =>
                    log('team_nav_click', 'twitter', teamData.twitter || '')
                  }
                >
                  <Icons.Twitter />
                </SocHeadLink>
              </div>
            )}
          </TmHeader>
          {teamMedia.length > 0 && (
            <FeedCardList feedItems={teamMedia} section={'fav_team_home_section'} />
          )}
          <ChangeBtn onClick={clearFavTeam}>
            <p>Change favorite team</p>
          </ChangeBtn>
        </Box>
      )}
    </>
  )
}
